import PROMISED_PAYMENT_API from 'api/promised-payment/v1'
import WALLETS_API from 'api/wallets/v1'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import prepareFilterParams from 'utils/prepareFilterParams'
import { notificationShow } from 'modules/Notifications/actions'

export function* fetchWalletTransactions({ offset = 0 }) {
  const {
    activeWalletID: id,
    filters: filtersState,
    transactions: { limit },
  } = yield select((state) => ({
    activeWalletID: state['finance/wallets'].activeWalletID,
    transactions: state['finance/wallets'].transactions,
    filters: state['finance/wallets'].filters,
  }))

  const params = {
    ...prepareFilterParams(filtersState),
    limit,
    offset,
  }

  return yield call(WALLETS_API.fetchTransactions, { id, params })
}

export function* refetchWalletTransactions() {
  yield put({ type: 'WALLETS/RESET_WALLET_TRANSACTIONS' })

  yield call(getWalletTransactions)
}

export function* refetchWalletPromisedPayments({ payload }) {
  yield put({ type: 'WALLETS/RESET_WALLET_TRANSACTIONS' })

  yield call(fetchWalletPromisedPayments, payload)
}

export function* fetchWallets() {
  try {
    yield put({ type: 'WALLETS/SET_WALLETS', payload: { loading: true } })
    const { response, error } = yield call(WALLETS_API.fetchWallets)

    if (error) {
      throw error
    }

    yield put({
      type: 'WALLETS/SET_WALLETS',
      payload: { ...response, loaded: true },
    })
  } catch (error) {
    yield put({
      type: 'WALLETS/SET_WALLETS',
      payload: { loadFailed: true },
    })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({ type: 'WALLETS/SET_WALLETS', payload: { loading: false } })
  }
}

export function* getWalletTransactions(action) {
  const {
    transactions: { data: transactions = [] },
  } = yield select((state) => ({
    transactions: state['finance/wallets'].transactions,
  }))

  try {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        loading: true,
      },
    })

    const { response, error } = yield call(fetchWalletTransactions, {
      offset: action?.payload?.offset ?? 0,
    })

    if (error) {
      throw error
    }

    const { data = [], pagination, summary = {} } = response

    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        data: [...transactions, ...data],
        pagination,
        categories: summary,
        loaded: true,
      },
    })
  } catch (error) {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: { loadFailed: true },
    })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        loading: false,
      },
    })
  }
}

export function* fetchPromisedPayments({ payload = {} }) {
  try {
    yield put({
      type: 'WALLETS/SET_PROMISED_PAYMENTS',
      payload: {
        loading: true,
      },
    })

    const { response, error } = yield call(
      PROMISED_PAYMENT_API.fetchAll,
      payload,
    )

    if (error) {
      throw error
    }

    yield put({
      type: 'WALLETS/SET_PROMISED_PAYMENTS',
      payload: {
        data: response.data,
        loaded: true,
      },
    })
  } catch (error) {
    yield put({
      type: 'WALLETS/SET_PROMISED_PAYMENTS',
      payload: {
        loadFailed: true,
      },
    })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: 'WALLETS/SET_PROMISED_PAYMENTS',
      payload: {
        loading: false,
      },
    })
  }
}

export function* changeActiveWalletID({ payload }) {
  yield put({
    type: 'WALLETS/SET_ACTIVE_WALLET_ID',
    payload,
  })
}

export function* fetchWalletPromisedPayments({ payload }) {
  const {
    filters: filtersState,
    transactions: { limit, data: transactions = [], pagination },
    activeWalletID,
  } = yield select((state) => ({
    activeWalletID: state['finance/wallets'].activeWalletID,
    transactions: state['finance/wallets'].transactions,
    filters: state['finance/wallets'].filters,
  }))

  const params = {
    ...prepareFilterParams(filtersState),
    limit,
    wallets: [activeWalletID],
    ...payload,
  }

  try {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        loading: true,
      },
    })

    const { response, error } = yield call(PROMISED_PAYMENT_API.fetchAll, {
      params,
    })

    if (error) {
      throw error
    }

    const { data = [] } = response

    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        data: [...transactions, ...data],
        pagination: { ...pagination, ...response.pagination },
        loaded: true,
      },
    })
  } catch (error) {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: { loadFailed: true },
    })
    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )
    console.logXHR(error)
  } finally {
    yield put({
      type: 'WALLETS/SET_WALLET_TRANSACTIONS',
      payload: {
        loading: false,
      },
    })
  }
}

export default function* Sagas() {
  yield takeLatest('WALLETS/FETCH_WALLETS', fetchWallets)

  yield takeLatest('WALLETS/CHANGE_ACTIVE_WALLET_ID', changeActiveWalletID)

  yield takeLatest('WALLETS/FETCH_WALLET_TRANSACTIONS', getWalletTransactions)

  yield takeLatest(
    'WALLETS/REFETCH_WALLET_TRANSACTIONS',
    refetchWalletTransactions,
  )

  yield takeLatest(
    'WALLETS/REFETCH_WALLET_PROMISED_PAYMENTS',
    refetchWalletPromisedPayments,
  )

  yield takeLatest('WALLETS/FETCH_EXPIRED_PAYMENTS', fetchPromisedPayments)

  yield takeLatest(
    'WALLETS/FETCH_WALLET_PROMISED_PAYMENTS',
    fetchWalletPromisedPayments,
  )
}
